import React, { useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import Page from "~/components/Page";
import {
  Display01,
  Paragraph01,
  Paragraph03,
  Paragraph04,
  Label01,
} from "~/components/Texts";
import Logo from "~/components/logo";
import {
  Box,
  Flex,
  Stack,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormErrorMessage,
  useToast,
  Button,
  Input,
  useColorModeValue,
  DarkMode,
  LightMode,
} from "@chakra-ui/react";
import { FiCheck } from "react-icons/fi";
import { FaUserAlt, FaFacebookF, FaGoogle, FaTwitter } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { Formik, Field } from "formik";
import { Desktop } from "~/components/MediaQuery";
import { validateUsername, validateEmail } from "~/utils/InputValidators";
import {
  authWithEmailLink,
  authWithGoogle,
  authWithFacebook,
  authWithTwitter,
} from "~/actions/AuthActions";
import ConfirmEmail from "~/components/animations/ConfirmEmail";
import MotionBox from "~/components/MotionBox";
import { useTheme } from "@chakra-ui/react";
import Img from "react-optimized-image";
import SideImage from "~/public/signup_side_image.jpg";
import BackgroundImage from "~/public/marketing/landing_hero_background.jpg";

export default function SignUp() {
  return (
    <Page title="Creá tu tienda | Shop Story">
      <Box position="fixed">
        <Background />
      </Box>
      <Flex
        position="absolute"
        width="100vw"
        minHeight="100vh"
        justify="center"
        align="center"
      >
        <Foreground />
      </Flex>
    </Page>
  );
}

function Background() {
  return (
    <Flex minHeight="100vh" minWidth="100vw">
      {/* Left Panel */}
      <Box flex="2" backgroundColor="light.brand.strong">
        <Img
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          src={BackgroundImage}
          webp
          sizes={[300, 600, 900, 1200, 1600]}
          densities={[1, 2]} // Retina support
          draggable="false"
          loading="lazy"
        />
      </Box>

      {/* Right Panel  */}
      <Desktop position="relative" flex="1">
        <Img
          style={{
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "0% 0%",
          }}
          src={SideImage}
          webp
          sizes={[256, 450, 640]}
          densities={[1, 2]} // Retina support
          draggable="false"
          loading="lazy"
        />
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          backgroundColor="rgba(0,0,0,0.3)"
        />
      </Desktop>
    </Flex>
  );
}

function Foreground() {
  return (
    <Flex
      direction="column"
      justify="start"
      minHeight="100vh"
      width={{ mobile: "90%", desktop: "80%" }}
      maxW="1200px"
    >
      <Box pt="12" pb="6">
        <DarkMode>
          <Link href="/">
            <Button variant="unstyled">
              <Logo />
            </Button>
          </Link>
        </DarkMode>
      </Box>

      <Flex flex="1" justify="center" alignItems="center" pb="6">
        <Desktop flex="1">
          <LeftPanel />
        </Desktop>
        <RightPanel />
      </Flex>
    </Flex>
  );
}

function LeftPanel() {
  const variants = {
    left: { x: -100, opacity: 0 },
    center: { x: 0, opacity: 1 },
  };

  return (
    <MotionBox initial="left" animate="center" variants={variants}>
      <Stack spacing="6" pr="6" color="light.background.superLite">
        <Display01>La historia de tu negocio comienza ahora.</Display01>
        <Paragraph01>
          <Link href="/pricing">
            <a>
              <b>Comenzá gratis, sólo pagás después de vender</b>
            </a>
          </Link>
        </Paragraph01>
        <Stack>
          <Feature>Sin costos fijos</Feature>
          <Feature>Sin límite de ventas o visitas</Feature>
          <Feature>Sincronización con la nube</Feature>
          <Feature>Colaborades ilimitados por tienda</Feature>
          <Feature>Incluye Chat y Telescopio con visitantes</Feature>
          <Feature>Listo para comenzar a cobrar por internet</Feature>
        </Stack>
      </Stack>
    </MotionBox>
  );
}

function Feature(props) {
  return (
    <Stack direction="row" align="center">
      <FiCheck size="20" />
      <Paragraph03>{props.children}</Paragraph03>
    </Stack>
  );
}

function RightPanel() {
  const variants = {
    bottom: { y: 100, opacity: 0 },
    center: { y: 0, opacity: 1 },
  };
  const router = useRouter();
  const toast = useToast();
  const [doingAuth, setDoingAuth] = useState(false);
  const [otpEmailSent, setOtpEmailSent] = useState(false);

  function doSignupWithEmail(formValues, formikActions) {
    setDoingAuth(true);

    authWithEmailLink(formValues.email, formValues.username)
      .then(function () {
        formikActions.setSubmitting(false);
        setDoingAuth(false);
        setOtpEmailSent(true);
      })
      .catch(function (error) {
        formikActions.setSubmitting(false);
        setDoingAuth(false);

        if (
          error.code === "auth/email-already-in-use" ||
          error.code === "auth/account-exists-with-different-credential"
        )
          showErrorToast(
            "Ya existe una cuenta registrada para " + formValues.email
          );
        else showErrorToast("No pudimos crear tu cuenta en este momento");
      });
  }

  function doSocialLoginWithGoogle() {
    setDoingAuth(true);
    authWithGoogle()
      .then(function () {
        router.replace("/my-shops");
      })
      .catch(function (error) {
        setDoingAuth(false);
        if (
          error.code === "auth/email-already-in-use" ||
          error.code === "auth/account-exists-with-different-credential"
        )
          showErrorToast(
            "Ya existe una cuenta registrada con el email de tu cuenta de Google"
          );
        else showErrorToast("No pudimos crear tu cuenta en este momento");
      });
  }

  function doSocialLoginWithFacebook() {
    setDoingAuth(true);
    authWithFacebook()
      .then(function () {
        router.replace("/my-shops");
      })
      .catch(function (error) {
        setDoingAuth(false);

        if (
          error.code === "auth/email-already-in-use" ||
          error.code === "auth/account-exists-with-different-credential"
        )
          showErrorToast(
            "Ya existe una cuenta registrada con el email de tu cuenta de Facebook"
          );
        else showErrorToast("No pudimos crear tu cuenta en este momento");
      });
  }

  function doSocialLoginWithTwitter() {
    setDoingAuth(true);
    authWithTwitter()
      .then(function () {
        router.replace("/my-shops");
      })
      .catch(function (error) {
        if (
          error.code === "auth/email-already-in-use" ||
          error.code === "auth/account-exists-with-different-credential"
        )
          showErrorToast(
            "Ya existe una cuenta registrada con el email de tu cuenta de Twitter"
          );
        else showErrorToast("No pudimos crear tu cuenta en este momento");
        setDoingAuth(false);
      });
  }

  function showSuccesToast() {
    toast({
      title: "¡Bienvenido!",
      description: "Has iniciado sesión",
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  }

  function showErrorToast(rationale) {
    toast({
      title: "Upss",
      description: rationale,
      status: "error",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  }

  return (
    <Box flex="1" maxWidth="500px" width="100%">
      <MotionBox initial="bottom" animate="center" variants={variants}>
        <LightMode>
          <Box
            backgroundColor="light.background.superLite"
            borderRadius="brand"
            p="6"
            boxShadow="md"
          >
            <Stack>
              {!otpEmailSent && <FormHeader />}
              {!otpEmailSent && (
                <Form
                  onSubmit={(values, actions) =>
                    doSignupWithEmail(values, actions)
                  }
                  onGoogle={() => doSocialLoginWithGoogle()}
                  onFacebook={() => doSocialLoginWithFacebook()}
                  onTwitter={() => doSocialLoginWithTwitter()}
                  doingAuth={doingAuth}
                />
              )}
              {!otpEmailSent && <Disclaimer />}
              {otpEmailSent && <OtpEmailSent />}
            </Stack>
          </Box>
        </LightMode>
      </MotionBox>
    </Box>
  );
}

function FormHeader() {
  return (
    <Flex
      width="100%"
      align="center"
      justify="space-between"
      color="light.brand.strong"
    >
      <Label01>Creá tu cuenta</Label01>
      <Link href="/auth/login">
        <Button variant="link">Iniciar sesión</Button>
      </Link>
    </Flex>
  );
}

function Form(props) {
  const {
    onSubmit = () => {},
    onFacebook = () => {},
    onGoogle = () => {},
    onTwitter = () => {},
    doingAuth,
  } = props;
  const chakraTheme = useTheme();

  return (
    <Formik
      initialValues={{}}
      onSubmit={(values, actions) => {
        actions.setSubmitting(true);
        onSubmit(values, actions);
      }}
    >
      {(props) => (
        <form onSubmit={props.handleSubmit} style={{ width: "100%" }}>
          <Stack spacing="4" pt="4">
            <Field name="username" validate={validateUsername}>
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors.username && form.touched.username}
                >
                  <InputGroup size="lg">
                    <InputLeftElement
                      children={
                        <FaUserAlt
                          color={useColorModeValue(
                            chakraTheme.colors.light.brand.strong,
                            chakraTheme.colors.dark.brand.strong
                          )}
                        />
                      }
                    />
                    <Input {...field} type="text" placeholder="Nombre" />
                  </InputGroup>
                  <FormErrorMessage>{form.errors.username}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Field name="email" validate={validateEmail}>
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors.email && form.touched.email}
                >
                  <InputGroup size="lg">
                    <InputLeftElement
                      children={
                        <MdEmail
                          color={useColorModeValue(
                            chakraTheme.colors.light.brand.strong,
                            chakraTheme.colors.dark.brand.strong
                          )}
                        />
                      }
                    />
                    <Input
                      {...field}
                      type="email"
                      placeholder="Dirección de correo"
                    />
                  </InputGroup>
                  <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Button
              type="submit"
              width="100%"
              size="lg"
              variant="solid"
              isLoading={doingAuth}
            >
              Comenzar tu historia
            </Button>

            <SocialLogin
              isDisabled={doingAuth}
              onFacebook={() => onFacebook()}
              onGoogle={() => onGoogle()}
              onTwitter={() => onTwitter()}
            />
          </Stack>
        </form>
      )}
    </Formik>
  );
}

function SocialLogin({
  isDisabled = false,
  onFacebook = () => {},
  onGoogle = () => {},
  onTwitter = () => {},
}) {
  return (
    <Stack align="center" width="100%" spacing="4" pt="2">
      <Paragraph04>O creá tu cuenta con</Paragraph04>
      <Stack direction="row" flexWrap="wrap" width="100%" justify="center">
        {/* At the moment (26-08-2020), the Facebook App has been disabled due to compliance issues. We'll enable back this later.*/}
        {false && (
          <Button
            size="md"
            leftIcon={<FaFacebookF />}
            variant="outline"
            isDisabled={isDisabled}
            mb="2"
            onClick={() => onFacebook()}
          >
            Facebook
          </Button>
        )}
        <Button
          size="md"
          leftIcon={<FaGoogle />}
          variant="outline"
          isDisabled={isDisabled}
          mb="2"
          onClick={() => onGoogle()}
        >
          Google
        </Button>
        <Button
          size="md"
          leftIcon={<FaTwitter />}
          variant="outline"
          isDisabled={isDisabled}
          mb="2"
          onClick={() => onTwitter()}
        >
          Twitter
        </Button>
      </Stack>
    </Stack>
  );
}

function Disclaimer() {
  return (
    <Box pt="4">
      <Paragraph04
        color={useColorModeValue("light.brand.lite", "dark.brand.lite")}
      >
        Si te registras, significa que aceptas los{" "}
        <a href="/legal/terms-and-conditions">Términos del servicio</a> y la{" "}
        <a href="/legal/privacy-policy">Pólitica de privacidad</a>, incluido el
        Uso de cookies. Otras personas podrán encontrarte por tu correo
        electrónico o tu número de teléfono si los proporcionas.
      </Paragraph04>
    </Box>
  );
}

function OtpEmailSent() {
  return (
    <Stack spacing="6" width="100%">
      <Label01>Revisá tu correo</Label01>
      <ConfirmEmail />
      <Paragraph03 text-align="center" width="100%">
        <b>¡Todo listo!</b>
        <br />
        Acabamos de enviarte un correo electrónico con un enlace para iniciar tu
        sesión. No necesitas una contraseña :)
      </Paragraph03>
    </Stack>
  );
}
